/**
 * Shared Server-Sent Events (SSE) utilities for streaming operations.
 */

export interface ServerSentEvent {
  id?: string;
  event?: string;
  data: string;
}

/**
 * Check if an error is an AbortError (from AbortController).
 */
export const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === "AbortError";

/**
 * Parse a raw SSE chunk into a ServerSentEvent object.
 * Handles id, event, and data fields according to SSE spec.
 */
export const parseSseChunk = (chunk: string): ServerSentEvent | null => {
  const lines = chunk
    .split("\n")
    .map((line) => line.replace(/\r/g, ""))
    .filter((line) => line.trim().length > 0);

  const event: ServerSentEvent = { data: "" };

  for (const line of lines) {
    // Skip comments (lines starting with :)
    if (line.startsWith(":")) {
      continue;
    }

    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
      continue;
    }

    const field = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trimStart();

    switch (field) {
      case "id":
        event.id = value;
        break;
      case "event":
        event.event = value;
        break;
      case "data":
        event.data = event.data ? `${event.data}\n${value}` : value;
        break;
      default:
        break;
    }
  }

  if (event.data === "") {
    return null;
  }

  return event;
};

/**
 * Read events from a streaming response body.
 * Handles chunked responses and calls onEvent for each complete SSE message.
 */
export const readEventStream = async (response: Response, onEvent: (event: ServerSentEvent) => void): Promise<void> => {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Missing response body for event stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // SSE messages are separated by double newlines
    let boundaryIndex = buffer.indexOf("\n\n");

    while (boundaryIndex !== -1) {
      const rawEvent = buffer.slice(0, boundaryIndex);
      buffer = buffer.slice(boundaryIndex + 2);

      const parsed = parseSseChunk(rawEvent);
      if (parsed) {
        onEvent(parsed);
      }

      boundaryIndex = buffer.indexOf("\n\n");
    }
  }

  // Handle any remaining data in buffer
  buffer += decoder.decode();
  if (buffer.trim()) {
    const parsed = parseSseChunk(buffer);
    if (parsed) {
      onEvent(parsed);
    }
  }
};

/**
 * Build headers for SSE event stream requests.
 * Includes Last-Event-ID for resumable streams.
 */
export const buildEventHeaders = (
  accessToken: string,
  lastEventId: string | null,
  locale?: string,
): Record<string, string> => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    accept: "text/event-stream",
    "cache-control": "no-cache",
    ...(locale && { "Accept-Language": locale, "X-Acto-Locale": locale }),
  };

  if (lastEventId) {
    headers["Last-Event-ID"] = lastEventId;
  }

  return headers;
};
